import { Avatar, Box, Chip, Divider, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { PictureTile } from '../../../components/shared/tiles/PictureTile';
import { RecipeStep } from '../types';

interface RecipeBankMethodTabProps {
  steps: RecipeStep[];
  productionMethodName?: string | null;
}

/** Read-only production method steps - no add/edit/reorder affordances. */
const RecipeBankMethodTab = ({ steps, productionMethodName }: RecipeBankMethodTabProps) => {
  const { t } = useTranslation('recipeV2');

  if (!steps.length) {
    return <Typography color="text.secondary">{t('recipeBank.detail.noMethod', 'No method steps recorded for this recipe.')}</Typography>;
  }

  return (
    <Stack spacing={4}>
      {productionMethodName && (
        <Typography variant="textSM"
          color="text.secondary">{t('build.productionMethod', 'Production Method')}: {productionMethodName}</Typography>
      )}
      <Stack divider={<Divider />}>
        {[...steps].sort((left, right) => left.stepNumber - right.stepNumber).map((step) => (
          <Stack key={step.id}
            direction="row"
            spacing={3}
            alignItems="flex-start"
            sx={{ py: 3 }}>
            {step.mediaUrl && (
              <Box sx={{ width: 96, height: 72, flexShrink: 0, borderRadius: 1, overflow: 'hidden' }}>
                <PictureTile src={step.mediaUrl}
                  alt={step.title}
                  objectFit="cover" />
              </Box>
            )}
            <Avatar sx={{ width: 24, height: 24, fontSize: 13, bgcolor: 'grey.900', flexShrink: 0, mt: 0.25 }}>
              {step.stepNumber}
            </Avatar>
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="textSM">
                <Typography component="span"
                  variant="textSM"
                  fontWeight={700}>
                  {t('recipeBank.detail.step', 'Step {{number}}', { number: step.stepNumber })}
                </Typography>
                {' '}{step.instruction}
              </Typography>
              <Stack direction="row"
                spacing={1}
                flexWrap="wrap"
                useFlexGap
                sx={{ mt: 2 }}>
                {step.durationMinutes != null && (
                  <Chip size="small"
                    label={`${t('recipeBank.detail.duration', 'Duration')}: ${step.durationMinutes} min`} />
                )}
                {step.temperature && (
                  <Chip size="small"
                    label={`${t('recipeBank.detail.temperature', 'Temperature')}: ${step.temperature}`} />
                )}
                {step.equipment.map((equipment) => (
                  <Chip key={equipment.id}
                    size="small"
                    variant="outlined"
                    label={equipment.name} />
                ))}
              </Stack>
            </Box>
          </Stack>
        ))}
      </Stack>
    </Stack>
  );
};

export default RecipeBankMethodTab;
